import { Component, signal, ChangeDetectionStrategy, OnInit, OnDestroy, inject } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { FooterComponent } from './components/footer/footer';
import { NavbarComponent } from './components/navbar/navbar';
import { AuthenticatedUser, AuthenticationService } from '../services/authentication.service';
import { ThemeService } from '../services/theme.service';
import { MsalBroadcastService } from '@azure/msal-angular';
import { environment } from '../environments/environment';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { InteractionStatus } from '@azure/msal-browser';
import { registerChunkErrorRecovery } from '../services/chunk-error-handler.service';

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    MatButtonModule,
    MatToolbarModule,
    MatIconModule,
    MatProgressSpinnerModule,
    FooterComponent,
    NavbarComponent,
  ],
  templateUrl: './app.html',
  styleUrl: './app.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class App implements OnInit, OnDestroy {
  private readonly authService = inject(AuthenticationService);
  private readonly msalBroadcastService = inject(MsalBroadcastService);
  private readonly theme = inject(ThemeService)
  private readonly router = inject(Router);

  protected readonly isAuthenticated = signal(false);
  protected readonly authenticationError = signal<string | null>(null);
  protected readonly currentUser = signal<AuthenticatedUser | null>(null);
  protected readonly siteTitle = signal(this.getSiteTitle());

  private userSubscription?: Subscription;
  private msalSubscription?: Subscription;
  private startupErrorSubscription?: Subscription;

  ngOnInit(): void {
    registerChunkErrorRecovery(this.router);

    // First, check if we're returning from a redirect with an error
    const urlParams = new URLSearchParams(window.location.hash.substring(1));
    const error = urlParams.get('error');
    
    if (error) {
      this.isAuthenticated.set(true); // Stop the loading spinner
      return; // Don't try to authenticate
    }

    // Check if we have a token in the URL (successful auth)
    if (window.location.hash.includes('id_token')) {
      // Clear the redirect attempted flag since we're processing the callback
      sessionStorage.removeItem('hrm.redirect.attempted');
    }

    this.msalSubscription = this.msalBroadcastService.inProgress$
      .pipe(filter((status: InteractionStatus) => status === InteractionStatus.None))
      .subscribe(() => {
        this.authService.checkAndSetActiveAccount();
      });

    this.userSubscription = this.authService.getUser().subscribe((user) => {
      if (user) {
        this.currentUser.set(user);
        this.isAuthenticated.set(true);
        this.authenticationError.set(null);
       // console.log('✅ App should now be authenticated and visible');

        // Set current theme
        this.theme.apply(this.theme.theme());
      }
    });

    this.startupErrorSubscription = this.authService.getStartupError().subscribe((error) => {
      this.authenticationError.set(error);
    });
  }

  protected retryLogin(): void {
    this.authenticationError.set(null);
    this.authService.retryLogin();
  }

  ngOnDestroy(): void {
    this.userSubscription?.unsubscribe();
    this.msalSubscription?.unsubscribe();
    this.startupErrorSubscription?.unsubscribe();
  }

  private getSiteTitle(): string {
    const env = environment.environmentTitle;
    return env !== 'PROD'
      ? `HRM (Health & Risk Management) (${env})`
      : 'HRM (Health & Risk Management)';
  }
}